import type { Clip, Item, Staff, Voice } from '../content/types';

export const VOICE_LABEL: Record<Voice, string> = { female: 'Woman', male: 'Man', female2: 'Woman' };

/** Who says a line: the learner, or the other person in the lesson. */
export type Speaker = 'you' | 'staff';

/** The voices a phrase is recorded in. */
export function voicesOf(item: Item): Voice[] {
  return (['female', 'female2', 'male'] as const).filter((v) => item.audio[v]);
}

/**
 * Everyone speaks with their own voice, the one that matches their picture: the learner (a woman)
 * with the course's woman's voice, a man on the staff with the man's, and a woman on the staff with
 * a second woman's voice, so a conversation between two women still sounds like two people.
 */
export function speakerVoice(who: Speaker, staff: Staff): Voice {
  if (who === 'you') return 'female';
  return staff.voice === 'male' ? 'male' : 'female2';
}

/** The voice a phrase is heard in on its own: the person who says it in its lesson. */
export function itemVoice(item: Item, staff: Staff): Voice {
  return speakerVoice(item.role === 'say' ? 'you' : 'staff', staff);
}

/** A line in a scene, in the voice of whoever says it there. */
export function sceneVoice(_item: Item, who: Speaker, staff: Staff): Voice {
  return speakerVoice(who, staff);
}

const SAME_GENDER: Record<Voice, Voice> = { female: 'female2', female2: 'female', male: 'male' };

/** A phrase's clip in a voice; another of the same gender if that one is missing (tests keep it rare). */
export function clipFor(item: Item, voice: Voice): Clip {
  return item.audio[voice] ?? item.audio[SAME_GENDER[voice]] ?? item.audio[voicesOf(item)[0]]!;
}

/** The part of a clip with speech in it, padded a little, from its loudness bars. */
export function speechRange(clip: Clip): { from: number; to: number } {
  const bars = clip.peaks.length;
  if (!bars) return { from: 0, to: clip.duration };
  const first = clip.peaks.findIndex((p) => p >= 6);
  const last = bars - 1 - [...clip.peaks].reverse().findIndex((p) => p >= 6);
  if (first < 0) return { from: 0, to: clip.duration };
  const bar = clip.duration / bars;
  return { from: Math.max(0, (first - 1.5) * bar), to: Math.min(clip.duration, (last + 2.5) * bar) };
}

/** The loudness bars that fall inside a time range. */
export function barsIn(peaks: number[], duration: number, range: { from: number; to: number }): number[] {
  if (!peaks.length || duration <= 0) return peaks;
  const a = Math.max(0, Math.floor((range.from / duration) * peaks.length));
  const b = Math.min(peaks.length, Math.ceil((range.to / duration) * peaks.length));
  const slice = peaks.slice(a, Math.max(a + 1, b));
  const max = Math.max(...slice, 1);
  return slice.map((p) => Math.round((p / max) * 100));
}

/** The same number of bars for every waveform, so the model and the learner's recording look alike. */
export function resampleBars(bars: number[], count = 36): number[] {
  if (bars.length === 0) return new Array(count).fill(30);
  if (bars.length === 1) return new Array(count).fill(bars[0]);
  const out: number[] = [];
  for (let i = 0; i < count; i++) {
    const at = (i / (count - 1)) * (bars.length - 1);
    const lo = Math.floor(at);
    const hi = Math.min(bars.length - 1, lo + 1);
    out.push(Math.round(bars[lo] + (bars[hi] - bars[lo]) * (at - lo)));
  }
  return out;
}
